import { ForecastOpportunityMetadata, HierarchyLevel, TimePeriod, SalesDataPoint } from '../types'
import { getOpportunityColor } from './opportunities'
import { MONTHS_PER_WEEK } from './periods'
import { apply_door_overrides } from './line_setup'
import type { QuantityBasis, SkuPlanReference } from './quantity_basis'
import { round_cases } from "./quantity_basis"

// UNFI Warehouses from the attached file
const UNFI_WAREHOUSES = [
  "Atlanta", "Auburn", "Aurora", "Chesterfield", "Dayville", "Gilroy", 
  "Greenwood", "Howell", "Hudson Valley", "Iowa", "Lancaster", "Manchester", 
  "Moreno Valley", "Racine", "Philadelphia", "Richburg", "Ridgefield", 
  "Rocklin", "Sarasota", "Twin Cities", "York"
]

interface ProductLine {
  id: string
  name: string
  casesPerMonth: number
  costPerCase: number
  sameStoreSalesMultiplier: number
}

interface GrowthExpectation {
  id: string
  name: string
  initialDoors: number
  startMonth: string
  /** Last period with volume (inclusive); later periods are written as zeros. Unset = no end. */
  endMonth?: string
  storeCountGrowthMultiplier: number
  productLineName: string
  initialCasesPerMonth: number
  costPerCase: number
  sameStoreSalesMultiplier: number
  // SKU fields (only used when planning by SKU)
  productSku?: string
  skuName?: string
  // Catalog identity carried through from the SKU picker (see `sku_stable_key_for_expectation`).
  productId?: string
  productUpc?: string
  unitsPerCase?: number
  /** Door counts typed for particular months, overriding store growth there (`apply_door_overrides`). */
  doorsByPeriod?: Record<string, number>
}

/** One distributor's share of a customer's volume, as entered in the dialog (percent). */
export interface RtmDistributorSplit {
  name: string
  allocation_pct: number
}

interface CreateSalesSourceFormData {
  classOfTrade: string
  customer: string
  routeToMarket: string
  customerIsOther: boolean
  planningBySku: boolean | null
  isOpportunity?: boolean
  opportunityName?: string
  opportunityLikelihood?: number
  seasonalityEnabled?: boolean
  /** Jan..Dec, percent of baseline (100 = flat). */
  seasonalityFactorsPct?: number[]
  /** Distributor split for the ship-to view; falls back to `routeToMarket` at 100%. */
  rtmDistributors?: RtmDistributorSplit[]
  growthExpectations: GrowthExpectation[]
}

/**
 * What the plan a new sales source is joining looks like, so its rows are written the way the
 * rows around them are. Omitted, the generator writes the dialog's long-standing per-store shape.
 */
export interface SalesSourcePlanContext {
  quantity_basis: QuantityBasis
  /** How the plan already carries a SKU (price, plan units per case), by stable key. */
  sku_reference?: (stable_key: string | undefined) => SkuPlanReference | null
}

/**
 * How many of `periods` (already starting at the source's first period) carry volume: through
 * `end_period_key` inclusive, or all of them when there is no end or it is not in the list.
 */
export function active_period_count(periods: TimePeriod[], end_period_key: string | undefined): number {
  const end = (end_period_key ?? "").trim()
  if (!end) return periods.length
  const index = periods.findIndex((period: TimePeriod) => period.period_key === end)
  return index === -1 ? periods.length : index + 1
}

/**
 * Stable key for a dialog-created SKU: `sku:<upc>` (or `sku:<sku code>` when the catalog has
 * no UPC) — the importer convention `forecast_actualize` resolves against
 * `product_catalog_identifier.normalized_value` / `product.sku` (both matched lowercased).
 * Without this key, a SKU created on the page can never receive QuickBooks actuals.
 */
export function sku_stable_key_for_expectation(expectation: {
  productUpc?: string
  productSku?: string
}): string | undefined {
  const identity = (expectation.productUpc || expectation.productSku || "").trim().toLowerCase()
  return identity ? `sku:${identity}` : undefined
}

/**
 * `metadata.rtm` in the exact shape `ship_to_view.ts` reads: fractional allocations per
 * distributor. Only explicit split rows count — `routeToMarket` is a channel descriptor
 * ("DSD", "Indirect via Distributor"), not a distributor name, so it must never leak
 * into the ship-to grouping. No rows → undefined (the ship-to view's unmapped bucket).
 */
export function rtm_metadata_for_form(formData: {
  rtmDistributors?: RtmDistributorSplit[]
}): { source: string; distributors: { name: string; allocation: number }[] } | undefined {
  const explicit = (formData.rtmDistributors || [])
    .map((row: RtmDistributorSplit) => ({
      name: String(row.name || "").trim(),
      allocation: Number(row.allocation_pct) / 100,
    }))
    .filter((row) => row.name.length > 0 && Number.isFinite(row.allocation) && row.allocation > 0)
  if (explicit.length > 0) {
    return { source: "sales_source_dialog", distributors: explicit }
  }
  return undefined
}

/**
 * Jan..Dec multipliers from the dialog's percent inputs, or undefined when disabled/flat.
 * Values are clamped to [0, 1000]% so a typo cannot explode the plan.
 */
export function normalize_seasonality_factors(
  enabled: boolean | undefined,
  factors_pct: number[] | undefined
): number[] | undefined {
  if (!enabled || !factors_pct || factors_pct.length !== 12) return undefined
  const factors = factors_pct.map((pct: number) => {
    const clamped = Math.max(0, Math.min(1000, Number(pct)))
    return Number.isFinite(clamped) ? clamped / 100 : 1
  })
  return factors.every((factor: number) => factor === 1) ? undefined : factors
}

function createProductNodes(
  growthExpectations: GrowthExpectation[],
  parentId: string,
  periods: TimePeriod[],
  planningBySku: boolean | null,
  opportunity?: ForecastOpportunityMetadata,
  seasonality?: number[],
  plan?: SalesSourcePlanContext
): any[] {
  
  if (planningBySku) {
    // Create SKU nodes that roll up to product lines
    const productLineMap = new Map<string, any>()
    
    growthExpectations.forEach((expectation, index) => {
      
      const productLineName = expectation.productLineName
      const productLineId = `${parentId}-${productLineName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
      
      // Create or get existing product line
      if (!productLineMap.has(productLineId)) {
        const productLineNode = createNodeWithForecastConfig(
          productLineId,
          productLineName,
          HierarchyLevel.PRODUCT_LINE,
          parentId,
          0.030,
          0.018
        )
        applyOpportunityMetadata(productLineNode, opportunity)
        productLineNode.case_cost = expectation.costPerCase
        productLineNode.children = []
        productLineMap.set(productLineId, productLineNode)
      }
      
      // Create SKU node
      const skuId = `${productLineId}-${expectation.productSku?.toLowerCase().replace(/[^a-z0-9]/g, '-') || 'sku'}`
      const skuNode = createNodeWithForecastConfig(
        skuId,
        expectation.skuName || expectation.productSku || 'SKU',
        HierarchyLevel.SKU,
        productLineId,
        expectation.sameStoreSalesMultiplier,
        expectation.storeCountGrowthMultiplier,
        expectation.initialDoors,
        expectation.initialCasesPerMonth,
        expectation.costPerCase
      )
      applyOpportunityMetadata(skuNode, opportunity)

      skuNode.case_cost = expectation.costPerCase
      skuNode.product_sku = expectation.productSku
      skuNode.sku_name = expectation.skuName
      // Note: applyOpportunityMetadata sets an opportunity-scoped stable_key; the catalog
      // identity must not override it or the opportunity row would collide with baseline.
      const catalog_stable_key = sku_stable_key_for_expectation(expectation)
      if (catalog_stable_key && !opportunity) {
        skuNode.stable_key = catalog_stable_key
      }
      if (expectation.productId || expectation.unitsPerCase) {
        skuNode.metadata = {
          ...(skuNode.metadata || {}),
          ...(expectation.productId ? { catalog_product_id: expectation.productId } : {}),
          ...(expectation.unitsPerCase ? { units_per_case: expectation.unitsPerCase } : {}),
        }
      }
      
      // Generate sales data for this SKU
      const startPeriodIndex = periods.findIndex(p => p.period_key === expectation.startMonth)
      if (startPeriodIndex >= 0) {
        const baseStores = expectation.initialDoors
        const baseVolume = baseStores * expectation.initialCasesPerMonth * expectation.costPerCase
        
        
        const generated_periods = periods.slice(startPeriodIndex)
        skuNode.sales_data = createSalesData(
          generated_periods,
          baseStores,
          baseVolume,
          expectation.sameStoreSalesMultiplier,
          expectation.initialCasesPerMonth,
          // New sales sources are plan data: every generated period is forecast.
          periods[startPeriodIndex].period_key,
          expectation.costPerCase,
          opportunity,
          seasonality,
          {
            active_periods: active_period_count(generated_periods, expectation.endMonth),
            quantity_basis: plan?.quantity_basis,
            units_per_case: plan?.sku_reference?.(catalog_stable_key)?.units_per_case ?? null,
            store_growth_rate: expectation.storeCountGrowthMultiplier,
          }
        )
        // Doors typed for particular months in the dialog win over what growth gives there.
        skuNode.sales_data = apply_door_overrides(skuNode.sales_data, expectation.doorsByPeriod, plan?.quantity_basis)
        
      }
      
      // Add SKU to product line
      const productLine = productLineMap.get(productLineId)!
      productLine.children.push(skuNode)
    })
    
    const result = Array.from(productLineMap.values())
    return result
  } else {
    // Create product line nodes directly
    return growthExpectations.map((expectation, index) => {
      const productLineId = `${parentId}-${expectation.productLineName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
      const productLineNode = createNodeWithForecastConfig(
        productLineId,
        expectation.productLineName,
        HierarchyLevel.PRODUCT_LINE,
        parentId,
        expectation.sameStoreSalesMultiplier,
        expectation.storeCountGrowthMultiplier,
        expectation.initialDoors,
        expectation.initialCasesPerMonth,
        expectation.costPerCase
      )
      applyOpportunityMetadata(productLineNode, opportunity)
      
      productLineNode.case_cost = expectation.costPerCase
      
      // Generate sales data for this product line
      const startPeriodIndex = periods.findIndex(p => p.period_key === expectation.startMonth)
      
      if (startPeriodIndex >= 0) {
        const baseStores = expectation.initialDoors
        const baseVolume = baseStores * expectation.initialCasesPerMonth * expectation.costPerCase
        
        
        
        const generated_periods = periods.slice(startPeriodIndex)
        productLineNode.sales_data = createSalesData(
          generated_periods,
          baseStores,
          baseVolume,
          expectation.sameStoreSalesMultiplier,
          expectation.initialCasesPerMonth,
          // New sales sources are plan data: every generated period is forecast.
          periods[startPeriodIndex].period_key,
          expectation.costPerCase,
          opportunity,
          seasonality,
          {
            active_periods: active_period_count(generated_periods, expectation.endMonth),
            quantity_basis: plan?.quantity_basis,
            units_per_case: null,
            store_growth_rate: expectation.storeCountGrowthMultiplier,
          }
        )
        productLineNode.sales_data = apply_door_overrides(productLineNode.sales_data, expectation.doorsByPeriod, plan?.quantity_basis)
      }
      
      return productLineNode
    })
  }
}

/** What a generated row's periods look like beyond the dialog's raw inputs. */
interface SalesDataShape {
  /** Leading periods that carry volume; the rest are zeros (the source has an end). */
  active_periods?: number
  quantity_basis?: QuantityBasis
  /** Plan units in one case; unset or 0 leaves `units_sold` off the row. */
  units_per_case?: number | null
  /** Store-count growth per year (0.1 = 10%); unset means the doors stay put. */
  store_growth_rate?: number | null
}

/**
 * Growth after `months` months at an annual `rate`, compounded monthly: `(1 + rate)^(months/12)`.
 * Twelve months in, the factor is exactly `1 + rate`.
 */
export function annual_growth_factor(rate: number, months: number): number {
  const r = Number.isFinite(rate) ? rate : 0
  if (r <= -1) return 0
  return Math.pow(1 + r, months / 12)
}

function createSalesData(
  periods: TimePeriod[],
  baseStores: number,
  baseVolume: number,
  growthRate: number = 0.05,
  baseCases: number = Math.round(baseVolume / 50),
  forecastStartPeriod: string = "2025-01",
  caseCost: number = 10.0,
  opportunity?: ForecastOpportunityMetadata,
  seasonality?: number[],
  shape: SalesDataShape = {}
): SalesDataPoint[] {
  const active_periods = shape.active_periods ?? periods.length
  const quantity_is_total = shape.quantity_basis === "total"
  const units_per_case = Number(shape.units_per_case ?? 0)
  const storeGrowthRate = Number(shape.store_growth_rate ?? 0)

  const result = periods.map((period, index) => {
    const isForecast = period.period_key >= forecastStartPeriod
    // Dialog inputs are per **month**. On a week-grain version each period is one week, so
    // volume scales by 12/52.1775 and the growth exponent counts months the same way.
    const monthsPerPeriod = period.grain === "week" ? MONTHS_PER_WEEK : 1
    const months_elapsed = index * monthsPerPeriod
    // Both rates are **per year**, compounded month by month: 10% same-store growth lifts a
    // store's rate 10% over twelve months (December sits at 1.1^(11/12)), not 10% every month
    // (which put December at 2.85× January). Store count grows on its own rate; same-store
    // sales never add doors.
    const sssFactor = annual_growth_factor(growthRate, months_elapsed)
    const storeFactor = annual_growth_factor(storeGrowthRate, months_elapsed)
    // Seasonality shapes volume (cases / dollars), not distribution: the store count is the
    // doors you are in, which does not dip in a slow month.
    const seasonalFactor = seasonality?.[period.month - 1] ?? 1
    const volumeFactor = storeFactor * sssFactor * seasonalFactor * monthsPerPeriod
    const baseMetrics: SalesDataPoint["metrics"] = {
      store_count: Math.round(baseStores * storeFactor),
      volume_dollars: Math.round(baseVolume * volumeFactor),
      // Per-store plans carry one store's rate here, which grows with same-store sales only.
      cases_sold: round_cases(baseCases * sssFactor * seasonalFactor * monthsPerPeriod),
      case_cost: caseCost
    }
    if (quantity_is_total) {
      // The plan this row joins counts the account's whole volume, not one store's: cases are
      // doors × the per-store rate, and dollars are those cases at the case price, to the cent.
      const total_cases = baseStores * baseCases * volumeFactor
      baseMetrics.cases_sold = round_cases(total_cases)
      baseMetrics.volume_dollars = Math.round(total_cases * caseCost * 100) / 100
      if (units_per_case > 0) {
        baseMetrics.units_sold = Math.round(total_cases * units_per_case)
      }
    }
    if (index >= active_periods) {
      // Past the source's end: the row keeps its cells (so they stay editable) but carries nothing.
      baseMetrics.store_count = 0
      baseMetrics.volume_dollars = 0
      baseMetrics.cases_sold = 0
      if (baseMetrics.units_sold != null) {
        baseMetrics.units_sold = 0
      }
    }
    const opportunityMultiplier = opportunity && opportunity.status !== "live"
      ? Math.max(0, Math.min(100, opportunity.likelihood_pct)) / 100
      : 1
    const weighted_dollars = baseMetrics.volume_dollars * opportunityMultiplier
    return {
      period,
      metrics: {
        store_count: Math.round(baseMetrics.store_count * opportunityMultiplier),
        volume_dollars: quantity_is_total ? Math.round(weighted_dollars * 100) / 100 : Math.round(weighted_dollars),
        cases_sold: round_cases(baseMetrics.cases_sold * opportunityMultiplier),
        ...(baseMetrics.units_sold != null
          ? { units_sold: Math.round(baseMetrics.units_sold * opportunityMultiplier) }
          : {}),
        case_cost: caseCost
      },
      is_forecast: isForecast,
      source_kind: opportunity ? "opportunity" : undefined,
      data_source: opportunity?.name,
      opportunity_base_metrics: opportunity ? baseMetrics : undefined,
    }
  })
  
  
  return result
}

function applyOpportunityMetadata(node: any, opportunity?: ForecastOpportunityMetadata): void {
  if (!opportunity) return
  node.metadata = {
    ...(node.metadata || {}),
    opportunity,
  }
  node.stable_key = `${node.id}:opportunity:${opportunity.id}`
  node.path_key = `${node.parent_id || "root"}/${node.level}:${node.name.toLowerCase().trim()}:opportunity:${opportunity.id}`
}

function createNodeWithForecastConfig(
  id: string,
  name: string, 
  level: HierarchyLevel, 
  parent_id?: string,
  sameStoreSalesMultiplier: number = 0.03, 
  storeCountMultiplier: number = 0.02,
  initialDoors: number = 10,
  initialCasesPerMonth: number = 10,
  costPerCase: number = 10.0
): any {
  const node = {
    id,
    name, 
    level, 
    parent_id, 
    sales_data: [],
    forecast_config: {
      initial_doors: initialDoors,
      same_store_sales_multiplier: sameStoreSalesMultiplier,
      store_count_growth_multiplier: storeCountMultiplier,
      initial_cases_per_month: initialCasesPerMonth,
      cost_per_case: costPerCase
    }
  }
  
  
  return node
}

export interface GenerateSalesSourceDataResult {
  /** Roots to merge (class of trade or customer-level when COT omitted). */
  nodes: any[]
  /** Set when `isOpportunity`; used to expand the table to the new opportunity row. */
  opportunityId: string | null
}

export function generateSalesSourceData(
  formData: CreateSalesSourceFormData,
  periods: TimePeriod[],
  plan?: SalesSourcePlanContext
): GenerateSalesSourceDataResult {
  const cotProvided = formData.classOfTrade.trim().length > 0
  const classOfTradeName = formData.classOfTrade.trim()
  const classOfTradeId = classOfTradeName.toLowerCase().replace(/[^a-z0-9]/g, '-')
  const customerId = formData.customer.toLowerCase().replace(/[^a-z0-9]/g, '-')
  const opportunity: ForecastOpportunityMetadata | undefined = formData.isOpportunity ? {
    id: `opp_${Date.now()}`,
    name: formData.opportunityName?.trim() || `${formData.customer} opportunity`,
    likelihood_pct: Math.max(0, Math.min(100, Number(formData.opportunityLikelihood ?? 50))),
    status: "open",
    color: getOpportunityColor(Date.now()),
  } : undefined
  const opportunityIdSuffix = opportunity ? `-opportunity-${opportunity.id}` : ""
  const seasonality = normalize_seasonality_factors(formData.seasonalityEnabled, formData.seasonalityFactorsPct)

  // Calculate aggregate values from growth expectations for the class of trade node
  const totalInitialDoors = formData.growthExpectations.reduce((sum, exp) => sum + exp.initialDoors, 0)
  const totalInitialCases = formData.growthExpectations.reduce((sum, exp) => sum + exp.initialCasesPerMonth, 0)
  const avgCostPerCase = formData.growthExpectations.length > 0 
    ? formData.growthExpectations.reduce((sum, exp) => sum + exp.costPerCase, 0) / formData.growthExpectations.length
    : 0
  const avgSameStoreSalesMultiplier = formData.growthExpectations.length > 0
    ? formData.growthExpectations.reduce((sum, exp) => sum + exp.sameStoreSalesMultiplier, 0) / formData.growthExpectations.length
    : 0.035
  const avgStoreCountMultiplier = formData.growthExpectations.length > 0
    ? formData.growthExpectations.reduce((sum, exp) => sum + exp.storeCountGrowthMultiplier, 0) / formData.growthExpectations.length
    : 0.025

  const customerParentId = cotProvided ? `${classOfTradeId}${opportunityIdSuffix}` : undefined

  const customerNode = createNodeWithForecastConfig(
    `${customerId}${opportunityIdSuffix}`,
    formData.customer,
    HierarchyLevel.CUSTOMER,
    customerParentId,
    avgSameStoreSalesMultiplier,
    avgStoreCountMultiplier,
    totalInitialDoors,
    totalInitialCases,
    avgCostPerCase
  )
  applyOpportunityMetadata(customerNode, opportunity)
  if (seasonality) {
    // Rendered wherever node metadata is shown, and the record of what shaped the numbers.
    customerNode.metadata = {
      ...(customerNode.metadata || {}),
      seasonality: {
        source: "sales_source_dialog",
        factors_pct: formData.seasonalityFactorsPct,
      },
    }
  }
  const rtm = rtm_metadata_for_form(formData)
  if (rtm) {
    customerNode.metadata = {
      ...(customerNode.metadata || {}),
      rtm,
    }
  }

  const planningBySkuResolved =
    Boolean(formData.planningBySku) ||
    formData.growthExpectations.some(
      (exp: GrowthExpectation) => Boolean(exp.productSku?.trim()) || Boolean(exp.skuName?.trim())
    )

  const childNodes = createProductNodes(
    formData.growthExpectations,
    `${customerId}${opportunityIdSuffix}`,
    periods,
    planningBySkuResolved,
    opportunity,
    seasonality,
    plan
  )

  customerNode.children = childNodes

  if (!cotProvided) {
    return {
      nodes: [customerNode],
      opportunityId: opportunity ? opportunity.id : null,
    }
  }

  const classOfTradeNode = createNodeWithForecastConfig(
    `${classOfTradeId}${opportunityIdSuffix}`,
    classOfTradeName,
    HierarchyLevel.CLASS_OF_TRADE,
    undefined,
    avgSameStoreSalesMultiplier,
    avgStoreCountMultiplier,
    totalInitialDoors,
    totalInitialCases,
    avgCostPerCase
  )
  applyOpportunityMetadata(classOfTradeNode, opportunity)
  classOfTradeNode.children = [customerNode]

  return {
    nodes: [classOfTradeNode],
    opportunityId: opportunity ? opportunity.id : null,
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const sample_periods = [{ year: 2026, month: 1, period_key: "2026-01" }]

  describe("normalize_seasonality_factors", () => {
    it("returns undefined when disabled, malformed, or flat", () => {
      expect(normalize_seasonality_factors(false, Array(12).fill(90))).toBeUndefined()
      expect(normalize_seasonality_factors(true, undefined)).toBeUndefined()
      expect(normalize_seasonality_factors(true, [90, 100])).toBeUndefined()
      expect(normalize_seasonality_factors(true, Array(12).fill(100))).toBeUndefined()
    })

    it("converts percents to clamped multipliers", () => {
      const factors_pct = [90, 100, 100, 100, 100, 100, 100, 100, 100, 85, 85, 85]
      const factors = normalize_seasonality_factors(true, factors_pct)!
      expect(factors[0]).toBeCloseTo(0.9)
      expect(factors[9]).toBeCloseTo(0.85)
      expect(factors[1]).toBe(1)
      // A fat-fingered 100000% cannot explode the plan; a negative floor at zero.
      const clamped = normalize_seasonality_factors(true, [100000, -50, ...Array(10).fill(100)])!
      expect(clamped[0]).toBe(10)
      expect(clamped[1]).toBe(0)
    })
  })

  describe("seasonality in generated sales data", () => {
    const jan_oct_periods = [
      { year: 2027, month: 1, period_key: "2027-01" },
      { year: 2027, month: 10, period_key: "2027-10" },
    ]
    const form = (enabled: boolean) => ({
      classOfTrade: "",
      customer: "Publix",
      routeToMarket: "",
      customerIsOther: false,
      planningBySku: false,
      isOpportunity: false,
      seasonalityEnabled: enabled,
      seasonalityFactorsPct: [90, 100, 100, 100, 100, 100, 100, 100, 100, 85, 85, 85],
      growthExpectations: [
        {
          id: "g1",
          name: "",
          initialDoors: 100,
          startMonth: "2027-01",
          storeCountGrowthMultiplier: 0,
          productLineName: "Sparkling",
          initialCasesPerMonth: 100,
          costPerCase: 10,
          sameStoreSalesMultiplier: 0,
        },
      ],
    })

    it("shapes volume by month but never store count", () => {
      const res = generateSalesSourceData(form(true), jan_oct_periods)
      const points = res.nodes[0].children[0].sales_data
      // Jan: 100 stores × 100 cases × $10 × 90%; Oct at 85%. (cases_sold uses the
      // pre-existing generator semantics of initialCasesPerMonth as the monthly total.)
      expect(points[0].metrics.volume_dollars).toBe(90000)
      expect(points[0].metrics.cases_sold).toBe(90)
      expect(points[1].metrics.volume_dollars).toBe(85000)
      expect(points[0].metrics.store_count).toBe(100)
      expect(points[1].metrics.store_count).toBe(100)
      // The applied profile is recorded on the customer node.
      expect(res.nodes[0].metadata?.seasonality?.factors_pct?.[0]).toBe(90)
    })

    it("is inert when the toggle is off", () => {
      const res = generateSalesSourceData(form(false), jan_oct_periods)
      const points = res.nodes[0].children[0].sales_data
      expect(points[0].metrics.volume_dollars).toBe(100000)
      expect(res.nodes[0].metadata?.seasonality).toBeUndefined()
    })
  })

  describe("a source joining a plan that counts account totals", () => {
    const weeks = ["2027-W05", "2027-W06", "2027-W07", "2027-W08"].map((period_key: string, index: number) => ({
      year: 2027,
      month: 2,
      period_key,
      grain: "week" as const,
      label: `W0${index + 5}`,
    }))

    function qfc_form(extra: Record<string, unknown> = {}) {
      return {
        classOfTrade: "",
        customer: "Kroger - QFC",
        routeToMarket: "",
        customerIsOther: false,
        planningBySku: true,
        isOpportunity: false,
        growthExpectations: [
          {
            id: "g1",
            name: "",
            initialDoors: 45,
            startMonth: "2027-W05",
            storeCountGrowthMultiplier: 0,
            productLineName: "Sparkling Water - MP",
            // 3 cases per store per week, held in the form as its monthly equivalent.
            initialCasesPerMonth: 3 / MONTHS_PER_WEEK,
            costPerCase: 5.14,
            sameStoreSalesMultiplier: 0,
            productUpc: "840269200243",
            skuName: "Yuzu Sparkling Water (6-pack)",
            ...extra,
          },
        ],
      }
    }

    const plan: SalesSourcePlanContext = {
      quantity_basis: "total",
      sku_reference: (stable_key: string | undefined) =>
        stable_key === "sku:840269200243" ? { case_cost: 5.14, units_per_case: 1 } : null,
    }

    function sku_points(res: GenerateSalesSourceDataResult): any[] {
      return res.nodes[0].children[0].children[0].sales_data
    }

    it("writes doors × rate as the quantity, priced to the cent, with the plan's units", () => {
      const points = sku_points(generateSalesSourceData(qfc_form() as any, weeks, plan))
      expect(points[0].metrics.store_count).toBe(45)
      expect(points[0].metrics.cases_sold).toBe(135)
      expect(points[0].metrics.units_sold).toBe(135)
      expect(points[0].metrics.volume_dollars).toBe(693.9)
    })

    it("leaves units off when the plan has no unit pack for the SKU", () => {
      const points = sku_points(generateSalesSourceData(qfc_form({ productUpc: "000" }) as any, weeks, plan))
      expect(points[0].metrics.cases_sold).toBe(135)
      expect(points[0].metrics.units_sold).toBeUndefined()
    })

    it("keeps the per-store shape when no plan context is given", () => {
      const points = sku_points(generateSalesSourceData(qfc_form() as any, weeks))
      expect(points[0].metrics.cases_sold).toBe(3)
      expect(points[0].metrics.volume_dollars).toBe(694)
    })

    it("zeroes every period after the end, and keeps their cells", () => {
      const points = sku_points(generateSalesSourceData(qfc_form({ endMonth: "2027-W06" }) as any, weeks, plan))
      expect(points.map((p: any) => p.metrics.cases_sold)).toEqual([135, 135, 0, 0])
      expect(points.map((p: any) => p.metrics.store_count)).toEqual([45, 45, 0, 0])
      expect(points[3].metrics.volume_dollars).toBe(0)
      expect(points[3].metrics.units_sold).toBe(0)
    })
  })

  describe("growth rates are per year, compounded by month (1st Phorm, 10/27)", () => {
    const year = Array.from({ length: 12 }, (_: unknown, i: number) => ({
      year: 2027,
      month: i + 1,
      period_key: `2027-${String(i + 1).padStart(2, "0")}`,
    }))
    function growth_form(store_growth: number, sss: number) {
      return {
        classOfTrade: "",
        customer: "Target",
        routeToMarket: "",
        customerIsOther: false,
        planningBySku: false,
        isOpportunity: false,
        growthExpectations: [
          {
            id: "g1",
            name: "",
            initialDoors: 100,
            startMonth: "2027-01",
            storeCountGrowthMultiplier: store_growth,
            productLineName: "Bars",
            initialCasesPerMonth: 1,
            costPerCase: 10,
            sameStoreSalesMultiplier: sss,
          },
        ],
      } as any
    }
    const plan: SalesSourcePlanContext = { quantity_basis: "total", sku_reference: () => null }

    it("annual_growth_factor is 1 + rate after twelve months and 1 at the start", () => {
      expect(annual_growth_factor(0.1, 0)).toBe(1)
      expect(annual_growth_factor(0.1, 12)).toBeCloseTo(1.1, 10)
      expect(annual_growth_factor(0, 7)).toBe(1)
      expect(annual_growth_factor(-1, 3)).toBe(0)
    })

    it("10% store growth adds doors across the year and never changes the per-store rate", () => {
      const points = generateSalesSourceData(growth_form(0.1, 0), year, plan).nodes[0].children[0].sales_data
      expect(points[0].metrics.store_count).toBe(100)
      expect(points[0].metrics.cases_sold).toBe(100)
      expect(points[0].metrics.volume_dollars).toBe(1000)
      // December: 100 × 1.1^(11/12) ≈ 109.13 doors, each still at 1 case ($1,091.3). Doors are
      // whole; the cases keep the fraction the rate implies.
      expect(points[11].metrics.store_count).toBe(109)
      expect(points[11].metrics.cases_sold).toBe(109.13)
      expect(points[11].metrics.volume_dollars).toBeCloseTo(1091.3, 0)
    })

    it("10% same-store growth lifts a store's rate 10% over the year, never the door count", () => {
      const points = generateSalesSourceData(growth_form(0, 0.1), year, plan).nodes[0].children[0].sales_data
      expect(points.map((p: any) => p.metrics.store_count)).toEqual(Array(12).fill(100))
      expect(points[0].metrics.volume_dollars).toBe(1000)
      // Not 1000 × 1.1^11 = 2,853: December sits at 1.1^(11/12).
      expect(points[11].metrics.volume_dollars).toBeCloseTo(1091.3, 0)
    })

    it("counts the account's cases, not one store's, on a plan that counts totals", () => {
      const points = generateSalesSourceData(growth_form(0, 0), year, plan).nodes[0].children[0].sales_data
      expect(points[5].metrics.cases_sold).toBe(100)
      expect(points[5].metrics.volume_dollars).toBe(1000)
    })
  })

  describe("active_period_count", () => {
    const periods = ["2027-W05", "2027-W06", "2027-W07"].map((period_key: string) => ({ year: 2027, month: 2, period_key }))

    it("counts through the end period, inclusive", () => {
      expect(active_period_count(periods, "2027-W05")).toBe(1)
      expect(active_period_count(periods, "2027-W06")).toBe(2)
    })

    it("has no end when none is set or the end is off the list", () => {
      expect(active_period_count(periods, undefined)).toBe(3)
      expect(active_period_count(periods, "")).toBe(3)
      expect(active_period_count(periods, "2026-W01")).toBe(3)
    })
  })

  describe("sku identity from the catalog", () => {
    function sku_form(extra: Record<string, unknown>) {
      return {
        classOfTrade: "",
        customer: "Publix",
        routeToMarket: "",
        customerIsOther: false,
        planningBySku: true,
        isOpportunity: false,
        growthExpectations: [
          {
            id: "g1",
            name: "",
            initialDoors: 10,
            startMonth: "2026-01",
            storeCountGrowthMultiplier: 0,
            productLineName: "Sparkling",
            initialCasesPerMonth: 10,
            costPerCase: 10,
            sameStoreSalesMultiplier: 0,
            productSku: "SZ-001",
            skuName: "Yuzu 12oz",
            ...extra,
          },
        ],
      }
    }

    function first_sku(res: GenerateSalesSourceDataResult): any {
      return res.nodes[0].children[0].children[0]
    }

    it("keys a catalog-picked SKU as sku:<upc> so actualization can resolve it", () => {
      const res = generateSalesSourceData(
        sku_form({ productUpc: "840269200397", productId: "prod-1", unitsPerCase: 12 }) as any,
        sample_periods
      )
      const sku = first_sku(res)
      // Matches the importer convention (`sku:<upc>`) that forecast_actualize resolves;
      // without it, dialog-created SKUs can never receive QuickBooks actuals.
      expect(sku.stable_key).toBe("sku:840269200397")
      expect(sku.metadata?.catalog_product_id).toBe("prod-1")
      expect(sku.metadata?.units_per_case).toBe(12)
    })

    it("writes a door count typed for one month into that month only", () => {
      const two_months = [
        { year: 2026, month: 1, period_key: "2026-01" },
        { year: 2026, month: 2, period_key: "2026-02" },
      ]
      const res = generateSalesSourceData(sku_form({ doorsByPeriod: { "2026-02": 25 } }) as any, two_months)
      const rows = first_sku(res).sales_data
      expect(rows[0].metrics.store_count).toBe(10)
      expect(rows[1].metrics.store_count).toBe(25)
      // Per-store plan: the case rate holds, revenue follows the doors.
      expect(rows[1].metrics.cases_sold).toBe(rows[0].metrics.cases_sold)
      expect(rows[1].metrics.volume_dollars).toBe(rows[0].metrics.volume_dollars * 2.5)
    })

    it("falls back to the lowercased SKU code when no UPC is known", () => {
      const res = generateSalesSourceData(sku_form({}) as any, sample_periods)
      // Product.sku is matched trim().to_lowercase() server-side, so keep raw chars.
      expect(first_sku(res).stable_key).toBe("sku:sz-001")
    })
  })

  describe("route-to-market metadata", () => {
    function rtm_form(extra: Record<string, unknown>) {
      return {
        classOfTrade: "",
        customer: "NCG",
        routeToMarket: "",
        customerIsOther: false,
        planningBySku: false,
        isOpportunity: false,
        growthExpectations: [
          {
            id: "g1",
            name: "",
            initialDoors: 10,
            startMonth: "2026-01",
            storeCountGrowthMultiplier: 0,
            productLineName: "Sparkling",
            initialCasesPerMonth: 10,
            costPerCase: 10,
            sameStoreSalesMultiplier: 0,
          },
        ],
        ...extra,
      }
    }

    it("writes metadata.rtm from explicit distributor split rows", () => {
      const res = generateSalesSourceData(
        rtm_form({
          rtmDistributors: [
            { name: "UNFI East", allocation_pct: 60 },
            { name: "UNFI West", allocation_pct: 40 },
          ],
        }) as any,
        sample_periods
      )
      const rtm = res.nodes[0].metadata?.rtm
      // The exact shape ship_to_view.ts reads: fractional allocations under distributors.
      expect(rtm?.source).toBe("sales_source_dialog")
      expect(rtm?.distributors).toEqual([
        { name: "UNFI East", allocation: 0.6 },
        { name: "UNFI West", allocation: 0.4 },
      ])
    })

    it("never derives a split from the route-to-market channel descriptor", () => {
      // "DSD" / "Indirect via Distributor" are channel types, not distributor names —
      // leaking them into metadata.rtm would fabricate ship-to roots.
      const res = generateSalesSourceData(rtm_form({ routeToMarket: "DSD" }) as any, sample_periods)
      expect(res.nodes[0].metadata?.rtm).toBeUndefined()
    })

    it("writes no rtm metadata when no split rows are provided", () => {
      const res = generateSalesSourceData(rtm_form({}) as any, sample_periods)
      expect(res.nodes[0].metadata?.rtm).toBeUndefined()
    })
  })

  describe("generateSalesSourceData", () => {
    it("uses customer root when class of trade is omitted", () => {
      const res = generateSalesSourceData(
        {
          classOfTrade: "",
          customer: "KeHE",
          routeToMarket: "",
          customerIsOther: false,
          planningBySku: false,
          isOpportunity: false,
          growthExpectations: [
            {
              id: "g1",
              name: "",
              initialDoors: 1,
              startMonth: "2026-01",
              storeCountGrowthMultiplier: 0.02,
              productLineName: "Sparkling",
              initialCasesPerMonth: 10,
              costPerCase: 10,
              sameStoreSalesMultiplier: 0.03,
            },
          ],
        },
        sample_periods
      )
      expect(res.nodes).toHaveLength(1)
      expect(res.nodes[0].level).toBe(HierarchyLevel.CUSTOMER)
      expect(res.nodes[0].name).toBe("KeHE")
      expect(res.nodes[0].children?.length).toBeGreaterThan(0)
    })

    it("never sets class of trade name from customer when COT omitted", () => {
      const res = generateSalesSourceData(
        {
          classOfTrade: "",
          customer: "KeHE",
          routeToMarket: "",
          customerIsOther: false,
          planningBySku: false,
          isOpportunity: false,
          growthExpectations: [
            {
              id: "g1",
              name: "",
              initialDoors: 1,
              startMonth: "2026-01",
              storeCountGrowthMultiplier: 0.02,
              productLineName: "Sparkling",
              initialCasesPerMonth: 10,
              costPerCase: 10,
              sameStoreSalesMultiplier: 0.03,
            },
          ],
        },
        sample_periods
      )
      const roots = res.nodes
      const cot_named_kehe = roots.some(
        (n: any) => String(n.level) === HierarchyLevel.CLASS_OF_TRADE && n.name === "KeHE"
      )
      expect(cot_named_kehe).toBe(false)
    })
  })
}
